// Public FAQ page — 60+ Q&A across 8 categories.
//
// - Search field filters questions live (case-folded substring, no fuzzy
//   lib to avoid pulling fuse.js for marketing only).
// - Category tabs filter results in addition to search.
// - Each accordion item has a stable id so #faq-<slug> deep-links work.
// - On mount, if the hash matches an entry, that accordion opens and
//   scrolls into view.
// - FAQPage JSON-LD emits every entry on the page (search/tab filters
//   are client-only — schema covers the canonical content).

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link as LinkIcon, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { buildBreadcrumbJsonLd, buildFAQPageJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { FAQ_CATEGORIES, FAQ_ENTRIES, entryAnchor, faqAnswer, faqCategoryLabel, faqQuestion, type FaqCategory } from "@/lib/marketing-faq";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

type CategoryFilter = "all" | FaqCategory;

function foldText(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export default function FaqPage() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const location = useLocation();

  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  // Deep-link: open and scroll to entry whose anchor matches the hash.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const target = FAQ_ENTRIES.find((e) => entryAnchor(e) === hash);
    if (!target) return;
    setOpenItem(target.id);
    setActiveCategory("all");
    // Wait one frame so the accordion is in the DOM before scrolling.
    requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.hash]);

  const filtered = useMemo(() => {
    const folded = foldText(query.trim());
    return FAQ_ENTRIES.filter((e) => {
      if (activeCategory !== "all" && e.category !== activeCategory) return false;
      if (!folded) return true;
      const q = faqQuestion(e, locale);
      const a = faqAnswer(e, locale);
      return foldText(q).includes(folded) || foldText(a).includes(folded);
    });
  }, [query, activeCategory, locale]);

  // FAQPage schema covers EVERY entry so search engines index the full
  // set even though the user-facing list is filterable. This matches
  // Google's guidance: do not vary structured data based on client UI.
  const jsonLd = useMemo(
    () => [
      buildOrganizationJsonLd(),
      buildBreadcrumbJsonLd([
        { name: t("marketing.breadcrumbs.home"), path: "/" },
        { name: t("marketing.footer.columns.resources.links.faq"), path: "/faq" },
      ]),
      buildFAQPageJsonLd(
        FAQ_ENTRIES.map((e) => ({
          question: faqQuestion(e, locale),
          answer: faqAnswer(e, locale),
        })),
      ),
    ],
    [t, locale],
  );

  async function copyAnchorLink(entryId: string) {
    const url = `${window.location.origin}/faq#faq-${entryId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("marketing.pages.faq.hero.anchorCopiedToast"));
    } catch {
      // Older browsers / lock-screen contexts — silent no-op is fine.
    }
  }

  return (
    <>
      <SeoHead
        title={t("marketing.pages.faq.seo.title")}
        description={t("marketing.pages.faq.seo.description")}
        path="/faq"
        locale={locale}
        jsonLd={jsonLd}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.faq.hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">
            {t("marketing.pages.faq.hero.title")}
          </DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
            {t("marketing.pages.faq.hero.lead")}
          </p>
        </div>

        <div className="mt-10 max-w-2xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("marketing.pages.faq.hero.searchPlaceholder")}
              aria-label={t("marketing.pages.faq.hero.searchAriaLabel")}
              className="h-12 pl-10 text-base"
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("marketing.pages.faq.hero.resultsCount", { count: filtered.length })}
          </p>
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <Tabs
          value={activeCategory}
          onValueChange={(v) => setActiveCategory(v as CategoryFilter)}
          className="w-full"
        >
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <TabsTrigger
              value="all"
              className="border border-rule data-[state=active]:border-foreground data-[state=active]:bg-foreground data-[state=active]:text-background"
            >
              {t("marketing.pages.faq.hero.allCategories")}
            </TabsTrigger>
            {FAQ_CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                className="border border-rule data-[state=active]:border-foreground data-[state=active]:bg-foreground data-[state=active]:text-background"
              >
                {faqCategoryLabel(c, locale)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {filtered.length === 0 ? (
          <p className="mt-10 text-center text-sm text-muted-foreground">
            {t("marketing.pages.faq.hero.noResults")}
          </p>
        ) : (
          <Accordion
            type="single"
            collapsible
            value={openItem}
            onValueChange={setOpenItem}
            className="mt-8 border-y border-rule"
          >
            {filtered.map((entry) => {
              const anchor = entryAnchor(entry);
              const question = faqQuestion(entry, locale);
              const answer = faqAnswer(entry, locale);
              return (
                <AccordionItem
                  key={entry.id}
                  value={entry.id}
                  id={anchor}
                  className="border-b border-rule last:border-b-0"
                >
                  <div className="flex items-center justify-between gap-2 pr-2">
                    <AccordionTrigger className="flex-1 py-4 text-left text-base font-medium">
                      {question}
                    </AccordionTrigger>
                    <button
                      type="button"
                      onClick={() => copyAnchorLink(entry.id)}
                      className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={t("marketing.pages.faq.hero.anchorAriaLabel")}
                    >
                      <LinkIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                  <AccordionContent className="pb-4 pr-12 text-sm leading-relaxed text-muted-foreground">
                    {answer}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </Section>

      <Section padding="md" tone="paper">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="max-w-xl">
            <h2 className="text-xl font-medium tracking-tight">{t("marketing.pages.faq.missing.title")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t("marketing.pages.faq.missing.lead")}
            </p>
          </div>
          <Button asChild>
            <Link to="/contact?subject=faq">
              {t("marketing.pages.faq.missing.cta")}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
      </Section>
    </>
  );
}
